<template>
    <div>
        <b-modal
            :visible="visible"
            id="article-material-modal"
            size="lg"
            :ok-title="`${selected_material ? 'Edit' : 'Add'} Material`"
            :ok-disabled="!validated && !saving"
            @hidden="hidden"
            @show="show"
            @ok.prevent="save"
        >
            <template #modal-title>
                <span v-if="selected_material">Edit</span>
                <span v-else>Add</span>
                Material
            </template>

            <b-form-group label="Name" label-for="article-material-modal-name">
                <b-form-input
                    id="article-material-modal-name"
                    type="text"
                    placeholder="Enter the material's name"
                    aria-label="name"
                    v-model="name"
                    required
                    autofocus
                    :disabled="saving"
                />
                <b-form-invalid-feedback :state="validated" v-if="!saving">
                    <span v-if="name.length === 0">
                        Name is required.
                    </span>
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Quantity" label-for="article-material-modal-quantity">
                <b-form-input
                    id="article-material-modal-quantity"
                    type="number"
                    aria-label="quantity"
                    v-model="quantity"
                    required
                    min="0"
                    :disabled="saving"
                />
            </b-form-group>

            <b-form-group>
                <template #label>
                    <label for="article-material-modal-distribution-type">Quantity Type</label>
                    <b-btn
                        v-b-tooltip.hover
                        title="How the material quantity should be distributed among students."
                        variant="link"
                        class="p-0 ml-2"
                        tabindex="-1"
                    >
                        <font-awesome-icon :icon="['fad', 'info-circle']"/>
                    </b-btn>
                </template>

                <b-form-select
                    v-model="per_type"
                    :options="per_type_options"
                    id="article-material-modal-distribution-type"
                    :disabled="saving"
                />
            </b-form-group>

            <b-form-group
                v-if="per_type === 'group'"
                label="# of Students"
            >
                <b-form-input
                    id="article-material-modal-group-size"
                    type="number"
                    aria-label="group quantity"
                    v-model="default_group_size"
                    min="0"
                    required
                    :disabled="saving"
                />
            </b-form-group>

            <b-form-group>
                <b-form-checkbox
                    id="checkbox-1"
                    v-model="check_teacher_notes"
                    name="checkbox-1"
                    :value="true"
                    :unchecked-value="false"
                    switch
                    :disabled="saving"
                >
                    Refer to Teacher Notes for additional information
                </b-form-checkbox>
            </b-form-group>

            <b-form-group class="mt-4" label="Printable">
                <b-btn
                    v-if="worksheet === null"
                    variant="outline-primary"
                    @click="show_worksheet_modal = true"
                    :disabled="saving"
                >
                    Attach Printable
                </b-btn>
                <div class="border-left pl-3" v-else>
                    <b-row>
                        <b-col>
                            <b-table-simple small borderless>
                                <b-tbody>
                                    <b-tr class="border-bottom">
                                        <b-th style="width: 25%">Title</b-th>
                                        <b-td style="width: 75%">
                                            <b-link v-if="worksheet.cdn_url" :href="worksheet.cdn_url" target="_blank">
                                                {{ worksheet.title }}
                                                <font-awesome-icon :icon="['fad', 'external-link']" fixed-width/>
                                            </b-link>
                                            <span v-else>
                                                {{ worksheet.title }}
                                            </span>
                                        </b-td>
                                    </b-tr>
                                    <b-tr class="border-bottom">
                                        <b-th style="width: 25%">ID</b-th>
                                        <b-td style="width: 75%">
                                            {{ worksheet.id }}
                                        </b-td>
                                    </b-tr>
                                    <b-tr class="border-bottom">
                                        <b-th style="width: 25%">Language</b-th>
                                        <b-td style="width: 75%">
                                            {{ worksheet.language }}
                                        </b-td>
                                    </b-tr>
                                </b-tbody>
                            </b-table-simple>
                        </b-col>
                        <b-col v-if="worksheet.cdn_url">
                            <embed
                                :src="worksheet.cdn_url"
                                width="100%"
                                height="100%"
                            >
                        </b-col>
                    </b-row>
                    <b-btn
                        size="sm"
                        variant="outline-danger"
                        @click="worksheet = null"
                        class="mt-2"
                        :disabled="saving"
                    >
                        Remove Printable
                    </b-btn>
                </div>
            </b-form-group>
        </b-modal>

        <attach-worksheets-modal
            v-model="show_worksheet_modal"
            @link="linkPrintable"
            @hide="show_worksheet_modal = false"
        />
    </div>
</template>

<script>
import AttachWorksheetsModal from '@/components/modals/article/worksheets/AttachWorksheetsModal'
import moment from 'moment'

const defaultFields = {
    name: '',
    worksheet: null,
    check_teacher_notes: false,
    quantity: 1,
    per_type: 'student',
    default_group_size: 1,
}

export default {
    data: () => ({
        ...defaultFields,
        saving: false,
        show_worksheet_modal: false,
        per_type_options: [
            {value: 'student', text: 'Per Student'},
            {value: 'group', text: 'Per # of students'},
            {value: 'classroom', text: 'Per Classroom'},
            {value: 'as_needed', text: 'As needed'},
        ]
    }),
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        selected_material: {
            type: Object,
            default: null,
        },
    },
    emits: ['update:visible', 'create:material', 'update:material'],
    components: {AttachWorksheetsModal},
    computed: {
        validated() {
            return (
                this.name.length > 0
            )
        },
    },
    methods: {
        reset() {
            for (const [k, v] of Object.entries(defaultFields)) {
                if (!this.selected_material) {
                    this[k] = v;
                } else {
                    this[k] = this.selected_material[k] !== undefined ? this.selected_material[k] : v;
                }
            }
        },
        hidden() {
            this.$emit('update:visible', false)
        },
        show() {
            this.reset()
        },
        linkPrintable(printable) {
            this.worksheet = printable
            // if we haven't set a name, we might want the printable's title as the name.
            if (this.name === '' && printable.title !== '') {
                this.name = printable.title
            }

            this.show_worksheet_modal = false
        },
        async save() {
            try {
                this.saving = true
                const materialData = {
                    name: this.name,
                    worksheet_id: (this.worksheet && this.worksheet.id) ? this.worksheet.id : null,
                    per_type: this.per_type,
                    quantity: this.quantity,
                    default_group_size: this.default_group_size,
                    check_teacher_notes: this.check_teacher_notes,
                }

                if (!this.selected_material) {
                    const response = await this.axios.post('/api/materials', materialData)
                    this.$emit('create:material', response.data)
                } else {
                    const response = await this.axios.patch(`/api/materials/${this.selected_material.id}`, materialData)
                    this.$emit('update:material', response.data)

                }

                this.$emit('update:visible', false)
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to create material: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.saving = false
            }
        },
    },
}
</script>
